import type { ApiError } from './globals';

/**
 * api.ts — la puerta a la API desde la capa React.
 *
 * Delega en el `API` global de `api.js`, y eso es deliberado: ahí viven el
 * token, el 401 centralizado que devuelve al login, y el `err.status` que
 * ErrorState necesita para distinguir un 403 de un 503. Reimplementarlo aquí
 * daría dos versiones de la sesión, y tarde o temprano se desincronizan.
 *
 * Lo que esta capa añade es el TIPO. `API.get('/perfil')` devuelve `unknown`
 * hasta que alguien le dice qué es; aquí se le dice una sola vez, en el sitio
 * donde se declara el endpoint, y no en cada pantalla que lo llama.
 *
 * Aviso honesto sobre esos tipos: hoy se escriben a mano porque el backend
 * declara UN solo `response_model` en 27 routers, así que `/openapi.json` no
 * describe ninguna respuesta y no hay nada que generar. En cuanto los routers
 * declaren su modelo, estos tipos se sustituyen por los generados y el
 * compilador empieza a avisar cuando el backend cambia de forma. Mientras
 * tanto son una promesa, no una garantía — y conviene que eso esté escrito.
 */

export type { ApiError };

export const api = {
  get: <T>(ruta: string, params?: Record<string, string | number>) =>
    API.get(ruta, params) as Promise<T>,
  post: <T>(ruta: string, cuerpo?: unknown) => API.post(ruta, cuerpo) as Promise<T>,
  put: <T>(ruta: string, cuerpo?: unknown) => API.put(ruta, cuerpo) as Promise<T>,
  del: <T>(ruta: string) => API.delete(ruta) as Promise<T>,
  upload: <T>(ruta: string, datos: FormData) => API.upload(ruta, datos) as Promise<T>,
};

/** El código HTTP de un error, si lo trae. Un fallo de red no lo trae, y eso
 *  también es información: significa «no llegamos a preguntar». */
export function estadoDe(err: unknown): number | undefined {
  return typeof err === 'object' && err !== null && 'status' in err
    ? (err as ApiError).status
    : undefined;
}
